'use client';

import { FC, useRef, useEffect, useMemo } from 'react';
import { Content, RichTextField } from '@prismicio/client';
import { PrismicRichText } from '@prismicio/react';
import Image from 'next/image';
import { LeftArrowIcon, RightArrowIcon } from '@/assets';
import { Job } from '@/interfaces/workable';
import { useWorkableJobs } from '@/hooks';
import { toJobCardModel } from '@/utils/workable';

type DefaultSlice = Extract<Content.CardGridSlice, { variation: 'default' }>;

export interface DefaultVariantProps {
  slice: DefaultSlice;
  initialJobs: Job[];
}

const DefaultVariant: FC<DefaultVariantProps> = ({ slice, initialJobs }) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  const { jobs, isLoading } = useWorkableJobs({ initialJobs });
  const cards = useMemo(() => jobs?.map(toJobCardModel) ?? [], [jobs]);

  useEffect(() => {
    if (cards.length === 0) {
      return;
    }

    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -10% 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-slide-in-from-bottom');
          entry.target.classList.remove('opacity-0', 'translate-y-12');
        }
      });
    }, observerOptions);

    const currentElements: (HTMLDivElement | null)[] = [];

    cardRefs.current.forEach((card, index) => {
      if (card) {
        currentElements.push(card);
        card.classList.add(
          'opacity-0',
          'translate-y-12',
          'transition-all',
          'duration-700',
          'ease-out',
        );
        card.style.transitionDelay = `${index * 100}ms`;
        observer.observe(card);
      }
    });

    return () => {
      currentElements.forEach((element) => {
        if (element) {
          observer.unobserve(element);
        }
      });
    };
  }, [cards]);

  const scroll = (scrollOffset: number) => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: scrollOffset,
        behavior: 'smooth',
      });
    }
  };

  const prismicImages =
    slice.primary.items?.map((item) => item.image).filter(Boolean) || [];

  const getImageForJob = (index: number) => {
    if (prismicImages.length === 0) {
      return null;
    }
    return prismicImages[index % prismicImages.length];
  };

  if (isLoading) {
    return (
      <section
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        className="py-8 bg-[#0F423A] overflow-x-hidden relative"
      >
        <div className="container mx-auto px-16">
          <div className="flex items-center justify-center h-40">
            <p className="text-white text-lg">Loading job positions...</p>
          </div>
        </div>
      </section>
    );
  }

  if (cards.length === 0) {
    return (
      <section
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        className="py-8 bg-[#0F423A] overflow-x-hidden relative"
      >
        <div className="container mx-auto px-16">
          <div className="flex items-center justify-center h-40">
            <p className="text-white text-lg">
              No job positions available at the moment.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="py-8 bg-[#0F423A] overflow-x-hidden relative"
    >
      <div className="container mx-auto px-16">
        <div
          ref={scrollContainerRef}
          className="flex gap-6 overflow-x-auto no-scrollbar -mx-16 px-16"
        >
          {cards.map((card, idx) => {
            const image = getImageForJob(idx);

            return (
              <div
                key={card.key}
                ref={(el) => {
                  cardRefs.current[idx] = el;
                }}
                className="min-w-[300px] rounded-[16px] bg-white/10 shadow-[0_4.325px_64.871px_0_rgba(0,0,0,0.08)] flex flex-col p-6"
              >
                {image?.url && (
                  <Image
                    src={image.url}
                    alt={image.alt ?? card.title}
                    width={400}
                    height={160}
                    className="rounded-[12px] w-full h-40 object-cover mb-4"
                  />
                )}
                <h3 className="text-lg font-semibold mb-2 text-white">
                  {card.title}
                </h3>
                <div className="flex items-center text-sm text-white/80 mb-4 gap-4">
                  {card.location && (
                    <span className="flex items-center gap-1">
                      {slice.primary.location_icon?.url && (
                        <Image
                          src={slice.primary.location_icon.url}
                          alt="location"
                          width={16}
                          height={16}
                          className="w-4 h-4"
                        />
                      )}
                      {card.location}
                    </span>
                  )}
                  {card.type && (
                    <span className="flex items-center gap-1">
                      {slice.primary.time_icon?.url && (
                        <Image
                          src={slice.primary.time_icon.url}
                          alt="type"
                          width={16}
                          height={16}
                          className="w-4 h-4"
                        />
                      )}
                      {card.type}
                    </span>
                  )}
                </div>
                <a
                  href={card.applyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-auto"
                >
                  <button className="border border-white rounded-full px-6 py-2 text-white hover:bg-white/20 transition w-full">
                    {Array.isArray(slice.primary.button_label) &&
                    slice.primary.button_label.length > 0 &&
                    typeof slice.primary.button_label[0] === 'object' ? (
                      <PrismicRichText
                        field={
                          slice.primary.button_label as unknown as RichTextField
                        }
                      />
                    ) : typeof slice.primary.button_label === 'string' ? (
                      slice.primary.button_label
                    ) : (
                      'Apply Now'
                    )}
                  </button>
                </a>
              </div>
            );
          })}
        </div>
      </div>
      {cards.length > 0 && (
        <div className="absolute top-1/2 -translate-y-1/2 flex justify-between w-full px-4 pointer-events-none">
          <button
            onClick={() => scroll(-300)}
            className="text-white hover:text-white/70 transition-colors pointer-events-auto"
            aria-label="Scroll left"
          >
            <LeftArrowIcon />
          </button>
          <button
            onClick={() => scroll(300)}
            className="text-white hover:text-white/70 transition-colors pointer-events-auto"
            aria-label="Scroll right"
          >
            <RightArrowIcon />
          </button>
        </div>
      )}
    </section>
  );
};

export default DefaultVariant;
